import { useState, useMemo } from 'react'
import {
  Box,
  Stack,
  FormHelperText,
  Typography,
  useTheme
} from '@mui/material'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { REGEX_MAC_ADDRESS } from '../use-actions'

const parseFile = (content) => {
  const [header, ...body] = content.indexOf('\r\n') > -1
    ? content.split('\r\n')
    : content.split('\n')
  const lines = ['mac_address;description', 'mac_address,description'].includes(header)
    ? body : [header, ...body]
  const separator = header.indexOf(';') > -1
    ? ';'
    : ','
  return lines
    .filter(line => line.length > 0)
    .reduce((acc, line) => {
      const [mac_address, description] = line.split(separator)
      return REGEX_MAC_ADDRESS.test(mac_address)
        ? {...acc, data: [...acc.data, {mac_address, description}]}
        : {...acc, errors: [...acc.errors, {mac_address, description}]}
    }, {errors: [], data: []})
}

const FilePicker = ({
  onFileChange
}) => {
  const [errors, setErrors] = useState([])
  const [lines, setLines] = useState([])
  const { t } = useTranslation()
  const theme = useTheme()

  const status = useMemo(() => {
    if (!errors.length && !lines.length) {
      return 'new'
    } else if (errors.length) {
      return 'error'
    }
    return 'success'
  }, [errors, lines])

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles?.[0] ?? null
    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = () => {
      const lines = parseFile(reader.result)
      if (lines.errors.length) {
        setErrors(lines.errors)
        setLines([])
      } else {
        setLines(lines.data)
        setErrors([])
        onFileChange(lines.data)
      }
    }
  }

  const {
    getRootProps,
    getInputProps,
    isDragReject
  } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    }
  })
  return (
    <Box
      {...getRootProps()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '160px',
        border: 'dotted 2px #DDD',
        borderRadius: '8px',
      }}
    >
      <input {...getInputProps()} />
      <Stack
        gap={1}
        alignItems="center"
        justifyContent="center"
        color="#DADDE3"
        sx={{
          height: 50
        }}
      >
        <i
          style={{fontSize: '32px', color: status === 'error' ? theme.palette.error.main : status === 'success' ? theme.palette.success.main: 'content.secondary'}}
          className={`fa-solid fa-${status === 'new' ? 'cloud-arrow-up' : status === 'error' ? 'warning' : 'circle-check'}`}
        />
        <Typography
          component={'span'}
          sx={{
            fontSize: '14px',
            lineHeight: '21px',
            color: status === 'error' ? 'error.main' : status === 'success' ? 'success.main': 'content.secondary',
            fontWeight: '500',
          }}
        >
          {
            status === 'error'
              ? t('bypass.import.error', {count: errors.length})
              : status === 'new'
                ? t('bypass.import.drop_text')
                : t('bypass.import.success', {count: lines.length})
          }

        </Typography>
        <Box
          px={1}
          sx={{
            borderRadius: 1,
            border: '1px solid #E4EEFA',
            backgroundColor: '#F2F8FD',
            color: 'primary.main',
            cursor: 'pointer'
          }}
        >
          <Typography component={'span'} sx={{fontSize: 12}}>
            {status === 'new' ? t('ui_elements.import') : t('ui_elements.import_retry')}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: '18px',
            color: 'content.tertiary',
            fontWeight: '400',
            textAlign: 'center',
          }}
        >
          <Stack alignItems="center">
            {isDragReject && (
              <FormHelperText error>
                {t('advertisements.edition.errors.drop_error')}
              </FormHelperText>
            )}
          </Stack>
        </Typography>
      </Stack>
    </Box>
  )
}


export default FilePicker
